/**
 * Helpers for the backend's 429 responses (see `backend/utils/error_handlers.py`).
 *
 * A 429 must never reach the user as a generic "failed to load" — on 2026-09-08
 * IO Plans reported rate-limited BI fetches as "Failed to load BI comparison
 * data", which sent Finance looking for a data problem that did not exist.
 *
 * Retry timing comes from the JSON body first and the `Retry-After` header
 * second: the header is only readable cross-origin when the API sends
 * `Access-Control-Expose-Headers`, the body always is.
 */
import axios from 'axios';

export interface RateLimitInfo {
  /** Whole seconds until the limit window resets, when the server said. */
  retryAfterSeconds: number | null;
  /** Wall-clock time the caller may retry, in the browser's timezone. */
  retryAt: Date | null;
}

const FALLBACK_MESSAGE = 'Too many requests. Please wait a moment and try again.';

/** Waits under this are phrased as a duration; longer ones as a clock time. */
const CLOCK_TIME_THRESHOLD_SECONDS = 90;

interface RateLimitBody {
  code?: string;
  details?: {
    retry_after_seconds?: unknown;
    retry_at?: unknown;
  };
}

function toPositiveSeconds(value: unknown): number | null {
  const parsed = typeof value === 'string' ? Number(value) : value;
  if (typeof parsed !== 'number' || !Number.isFinite(parsed) || parsed <= 0) return null;
  return Math.ceil(parsed);
}

function toDate(value: unknown): Date | null {
  if (typeof value !== 'string') return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** Retry timing for a 429, or `null` when `error` is any other failure. */
export function getRateLimitInfo(error: unknown): RateLimitInfo | null {
  if (!axios.isAxiosError(error) || error.response?.status !== 429) return null;

  const body = error.response.data as RateLimitBody | undefined;
  const retryAfterSeconds =
    toPositiveSeconds(body?.details?.retry_after_seconds) ??
    toPositiveSeconds(error.response.headers?.['retry-after']);

  const retryAt =
    toDate(body?.details?.retry_at) ??
    (retryAfterSeconds !== null ? new Date(Date.now() + retryAfterSeconds * 1000) : null);

  return { retryAfterSeconds, retryAt };
}

export function isRateLimitError(error: unknown): boolean {
  return getRateLimitInfo(error) !== null;
}

/** User-facing copy: always says "too many requests" and when to come back. */
export function rateLimitMessage(info: RateLimitInfo | null): string {
  if (!info) return FALLBACK_MESSAGE;

  const { retryAfterSeconds, retryAt } = info;

  if (retryAfterSeconds !== null && retryAfterSeconds <= CLOCK_TIME_THRESHOLD_SECONDS) {
    const unit = retryAfterSeconds === 1 ? 'second' : 'seconds';
    return `Too many requests. Peak paused your requests — try again in ${retryAfterSeconds} ${unit}.`;
  }

  if (retryAt) {
    const time = retryAt.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    return `Too many requests. Peak paused your requests — try again at ${time}.`;
  }

  return FALLBACK_MESSAGE;
}

/**
 * `rateLimitMessage` for any error, falling back to `otherwise` for the rest.
 * Keeps call sites from having to branch on the status themselves.
 */
export function describeError(error: unknown, otherwise: string): string {
  const info = getRateLimitInfo(error);
  return info ? rateLimitMessage(info) : otherwise;
}
